import {computed, inject, Injectable} from '@angular/core';
import {LoginFlowStore} from '../stores/login-flow.store';
import {SessionStore} from '../stores/session.store';
import {RedirectService} from '../services/redirect.service';
import {UserProfileService} from '../services/user-profile.service';
import {AuthTokens, LoginCredential} from '../models/auth.models';
import {catchError, firstValueFrom, of} from 'rxjs';
import {AuthApiService} from '../api/auth-api.service';
import {TokenStorageService} from '../services/token-storage.service';
import {UsersFacade} from '../../../features/users/users.facade';
import {TicketsFacade} from '../../../features/tickets/facades/tickets.facade';
import {TokenRefreshService} from '../services/token-refresh.service';
import {NotificationWebSocketService} from '../../../shared/components/notification-center/notification-web-socket.service';
import {LoginTransitionStore} from '../stores/login-transition.store';

@Injectable({ providedIn: 'root' })
export class AuthFacade {
	private loginFlow = inject(LoginFlowStore);
	private session = inject(SessionStore);
	private redirect = inject(RedirectService);
	private userProfile = inject(UserProfileService);
	private api = inject(AuthApiService);
	private tokenStorage = inject(TokenStorageService);
	private usersFacade = inject(UsersFacade);
	private ticketsFacade = inject(TicketsFacade);
	private tokenRefresh = inject(TokenRefreshService);
	private notificationWs = inject(NotificationWebSocketService);
	private loginTransition = inject(LoginTransitionStore);

	readonly tokens = computed<AuthTokens | null>(() =>
		this.loginFlow.tokens()
	);
	readonly loading = this.loginFlow.loading;
	readonly user = this.userProfile.profile;
	readonly error = this.loginFlow.error;
	readonly loginTransitionPhase = this.loginTransition.currentPhase;
	readonly loginTransitionCovered = this.loginTransition.isCovered;
	readonly loginTransitionExiting = this.loginTransition.isExiting;

	readonly tenants = computed(() =>
		this.user()?.available_tenants ?? []
	);

	readonly mailboxes = computed(() =>
		this.user()?.available_mailboxes ?? []
	);

	readonly requiresTenantChoice = computed(() =>
		this.session.isAuthenticated() &&
		!!this.user() &&
		!this.user()?.first_login &&
		!this.session.activeTenantId() &&
		this.tenants().length > 0
	);

	readonly requiresMailboxChoice = computed(() =>
		this.session.isAuthenticated() &&
		!!this.session.activeTenantId() &&
		!this.user()?.first_login &&
		!this.session.activeMailboxId() &&
		this.mailboxes().length > 0
	);

	readonly showLoginForm = computed(() =>
		!this.session.isAuthenticated()
	);

	constructor() {
		this.tokenRefresh.refreshFailed$
			.subscribe(() => this.logout());
	}

	async startLogin(credentials: LoginCredential) {
		await this.loginFlow.startLogin(credentials);

		const tokens = this.tokens();

		if (!tokens) return;

		await this.completeSession(tokens);
	}

	async chooseTenant(tenantId: string) {
		const tokens = await firstValueFrom(
			this.api.switchTenant(tenantId)
		);

		await this.completeSession(tokens);
	}

	async chooseMailbox(mailboxId: string) {
		const tokens = await firstValueFrom(
			this.api.switchMailbox(mailboxId)
		);

		await this.completeSession(tokens);
	}

	private async completeSession(tokens: AuthTokens) {
		this.tokenStorage.save(tokens);

		const decoded = this.session.startSession(tokens.jwt);

		this.tokenRefresh.scheduleFromToken(tokens.jwt);

		this.userProfile.setProfile(decoded.sub);

		if (decoded.sub.first_login) {
			this.redirect.toSetPassword();
			return;
		}

		await this.autoCompleteFlow();

	}

	private async autoCompleteFlow() {
		const tenant = this.session.activeTenantId();
		const mailbox = this.session.activeMailboxId();

		if (!tenant) {
			const tenants = this.tenants();
			if (tenants.length === 1) {
				await this.chooseTenant(tenants[0].tenant_id);
				return;
			}
			return;
		}

		if (!mailbox) {
			const mailboxes = this.mailboxes();
			if (mailboxes.length === 1) {
				await this.chooseMailbox(mailboxes[0].mailbox_id);
				return;
			}
			if (mailboxes.length > 1) {
				return;
			}
		}

		const user = this.userProfile.profile();

		if (user?.first_login) {
			this.redirect.toSetPassword();
			return;
		}

		if (!user?.active) {
			this.redirect.toPendingActivation();
			return;
		}

		await this.loginTransition.exitToDashboard();
		this.redirect.toDefaultRoute();
	}

	async setPassword(password: string) {
		await this.userProfile.setPassword(password);
		this.clearSession();
		this.redirect.toLogin();
	}

	logout() {
		this.api.logout().pipe(
			catchError(() => of(null))
		).subscribe();

		this.clearSession();
		this.redirect.toLogin();
	}

	private clearSession() {
		this.notificationWs.disconnect();
		this.tokenStorage.clear();
		this.session.logout();
		this.loginFlow.reset();
		this.loginTransition.reveal();
		this.tokenRefresh.clearTimer();
		this.userProfile.clear();
		this.usersFacade.clear();
		this.ticketsFacade.clear();
	}

}
